import { reactive } from 'vue';
import { useModal } from '@orion.ui/orion';
import BaseEntity from '@/entity/BaseEntity';
import TaskModalEdit from '@/components/task/TaskModalEdit.vue';
import useTaskService from '@/services/TaskService';
import type TempWorkerEntity from './temp-worker/TempWorkerEntity';

export default class TaskEntity extends BaseEntity<IUserTaskModel> {
	private modalEdit?: OrionModal;
	private readonly privateState = reactive({
		tempWorker: undefined as Undef<TempWorkerEntity>,
		isPerformingAjax: false,
	});

	get tempWorker (): TempWorkerEntity { return this.privateState.tempWorker as TempWorkerEntity; }
	get isPerformingAjax () { return this.privateState.isPerformingAjax; }

	get assignedUserLastName () { return this.state.assignedUserLastName; }
	get assignedUserFirstName () { return this.state.assignedUserFirstName; }
	get assignedUserAvatarId () { return this.state.assignedUserAvatarId; }

	get assignedUserId () { return this.state.assignedUserId; }
	set assignedUserId (val) { this.state.assignedUserId = val; }

	get title () { return this.state.title; }
	set title (val) { this.state.title = val; }

	get description () { return this.state.description; }
	set description (val) { this.state.description = val; }

	get icon () { return this.state.icon; }
	set icon (val) { this.state.icon = val; }

	get startDate () { return this.state.startDate ? new Date(this.state.startDate) : undefined; }
	set startDate (val) {
		if (val && this.duration) {
			this.state.endDate = new Date(val.getTime() + this.duration * 60 * 60000).toPost(true);
		}
		this.state.startDate = val?.toPost(true);
	}

	get endDate () { return this.state.endDate ? new Date(this.state.endDate) : undefined; }
	set endDate (val) { this.state.endDate = val?.toPost(true); }

	get duration () {
		if (!this.startDate || !this.endDate) return;
		return (new Date(this.endDate).valueOf() - new Date(this.startDate).valueOf()) / 3600 / 1000;
	}

	set duration (val) {
		if (this.startDate && val) {
			this.state.endDate = new Date(this.startDate.getTime() + +val * 60 * 60000).toPost(true);
		}
	}

	get isPassed () {
		if (!this.endDate) return false;

		const today = new Date();
		return this.endDate.valueOf() < today.valueOf();
	}

	get isFilled () {
		return this.state.assignedUserId && this.title && this.description && this.duration;
	}

	get readableDuration () {
		if (!this.startDate || !this.endDate) return;

		if (new Date(this.startDate).getHours() - new Date(this.endDate).getHours()) {
			return new Date(this.endDate).getHours() - new Date(this.startDate).getHours() + 'h';
		} else {
			return new Date(this.endDate).getMinutes() + 'min';
		}
	}

	get dbPayload (): IAddUserTaskModel {
		return {
			assignedUserId: this.assignedUserId,
			userId: this.tempWorker.id,
			description: this.description,
			title: this.title,
			icon: this.icon,
			endDate: this.endDate?.toJSON(),
			startDate: this.startDate?.toJSON(),
		};
	}


	constructor (payload?: IUserTaskModel) {
		super(payload);

		if (!payload) {
			this.assignData({
				id: 0,
				userId: 0,
				assignedUserId: this.self.id,
				icon: 'icon-interface-arrows-bend-down-right-2',
				title: undefined,
				description: undefined,
				startDate: undefined,
				endDate: undefined,
			});
		}
	}


	associateTempWorker (tempWorker: TempWorkerEntity) {
		this.privateState.tempWorker = tempWorker;
		this.state.userId = tempWorker.id;
		return this;
	}

	openModalEdit () {
		this.modalEdit = useModal({
			Nested: TaskModalEdit,
			NestedProps: { task: this },
			size: 'sm',
		});
	}

	closeModalEdit () {
		this.modalEdit?.close();
		this.modalEdit = undefined;
	}

	async storeAsync () {
		if (!this.isNew || !this._clone) return;
		const dbTask = await useTaskService().storeAsync(this._clone.dbPayload);
		this.tempWorker.addTask(dbTask);
		this.closeModalEdit();
	}

	async updateAsync () {
		if (this.isNew || !this._clone) return;
		const dbTask = await useTaskService().updateAsync(this.id, this._clone.dbPayload);
		this.assignData(dbTask);
		this.closeModalEdit();
	}

	async deleteAsync () {
		await useTaskService().deleteAsync(this.id);
		this.tempWorker.deleteTask(this.id);
	}
}
